import { api } from 'common/frontend/api';
import {
	newOnboardingActionCompletedEvent,
	OnboardingEventName,
	newOnboardingClickEvent,
} from 'common/services/analytics';
import { OnboardingAction, ShopAnalytics } from 'common/services/analytics/tractionAnalytics';
import { TFunction } from 'i18next';
import { Routes } from 'routing';

export type OnboardingItem = {
	title: string;
	done: boolean;
	onClick: () => void;
	routeTo?: string;
	routeHash?: string;
	hidden?: boolean;
};

export type OnboardingStep = {
	name: string;
	items: OnboardingItem[];
};

export type OnboardingStepsArgs = {
	onlineStoreUrl: string;
	t: TFunction;
	shopAnalytics: ShopAnalytics | undefined;
};

export const setOnboardingActionDone = (
	action: OnboardingAction,
	shopAnalytics: ShopAnalytics | undefined,
) => {
	if (!shopAnalytics || isOnboardingActionDone(action, shopAnalytics)) return;
	const analyticsDocId = shopAnalytics.id;
	const lowerCaseAction = action.toLowerCase() as OnboardingEventName;
	newOnboardingActionCompletedEvent(lowerCaseAction);
	return api()
		.shopAnalytics.doc(analyticsDocId)
		.update({
			[`onboardingActions.${action}` as `onboardingActions.${/*OnboardingAction*/ 'USERS'}`]: true,
		});
};

export const isOnboardingActionDone = (
	action: OnboardingAction,
	shopAnalytics: ShopAnalytics | undefined,
): boolean => {
	return shopAnalytics?.onboardingActions?.[action] === true;
};

export const getOnboardingSteps = (args: OnboardingStepsArgs): OnboardingStep[] => {
	const { onlineStoreUrl, t, shopAnalytics } = args;
	return [
		{
			name: t(
				'homeView.onboarding.testCoreFunctionalities',
				'Test the core functionalities of Rentle',
			),
			items: [
				{
					title: t('homeView.onboarding.createProductTitle', 'Create your first product'),
					routeTo: Routes.CATALOG_CREATE_PRODUCT,
					onClick: () => {
						newOnboardingClickEvent({ action: 'PRODUCT_CREATION' });
					},
					done: isOnboardingActionDone('PRODUCT_CREATION', shopAnalytics),
				},
				{
					title: t(
						'homeView.onboarding.onlineStoreTitle2',
						'Create your first order in your online store',
					),
					onClick: () => {
						newOnboardingClickEvent({ action: 'VISIT_ONLINE_STORE_BUTTON' });
						window.open(onlineStoreUrl, '_blank', 'noopener,noreferrer');
					},
					done: isOnboardingActionDone('BOOKING_CREATION', shopAnalytics),
				},
				{
					title: t(
						'homeView.onboarding.viewBookingsTitle2',
						'View and fulfill the order you created',
					),
					routeTo: Routes.BookingsTab('booked'),
					onClick: () => {
						newOnboardingClickEvent({ action: 'VISIT_BOOKINGS' });
					},
					done: isOnboardingActionDone('BOOKING_COMPLETION', shopAnalytics),
				},
			],
		},
		{
			name: t('homeView.onboarding.customizeOnlineStore', 'Customize your online store'),
			items: [
				{
					title: t('homeView.onboarding.addLogo', 'Add your logo'),
					routeTo: Routes.ACCOUNT_GENERAL,
					routeHash: '#appearance',
					onClick: () => {
						newOnboardingClickEvent({ action: 'LOGO' });
					},
					done: isOnboardingActionDone('LOGO', shopAnalytics),
				},
				{
					title: t(
						'homeView.onboarding.chooseAppearanceOfOnlineStore',
						'Choose the appearance of your online store',
					),
					routeTo: Routes.StoreTab('online-store'),
					onClick: () => {
						newOnboardingClickEvent({ action: 'APPEARANCE_ONLINE_STORE' });
					},
					done: isOnboardingActionDone('APPEARANCE_ONLINE_STORE', shopAnalytics),
				},
				{
					title: t('homeView.onboarding.verifyYourTaxRates', 'Verify your tax rates'),
					routeTo: Routes.ACCOUNT_TAXES,
					onClick: () => {
						newOnboardingClickEvent({ action: 'TAX_RATES' });
						setOnboardingActionDone('TAX_RATES', shopAnalytics);
					},
					done: isOnboardingActionDone('TAX_RATES', shopAnalytics),
				},
				{
					title: t(
						'homeView.onboarding.regionalFormatsTitle2',
						'Verify your currency and regional formats',
					),
					routeTo: Routes.ACCOUNT_LOCALIZATION,
					onClick: () => {
						newOnboardingClickEvent({ action: 'REGIONAL_FORMATS' });
						setOnboardingActionDone('REGIONAL_FORMATS', shopAnalytics);
					},
					done: isOnboardingActionDone('REGIONAL_FORMATS', shopAnalytics),
				},
				{
					title: t('homeView.onboarding.verifyLanguage', 'Verify your system language'),
					routeTo: Routes.ACCOUNT_LANGUAGES,
					onClick: () => {
						newOnboardingClickEvent({ action: 'VERIFY_LANGUAGE' });
						setOnboardingActionDone('VERIFY_LANGUAGE', shopAnalytics);
					},
					done: isOnboardingActionDone('VERIFY_LANGUAGE', shopAnalytics),
				},
			],
		},
		{
			name: t('homeView.onboarding.addInventory', 'Add your inventory'),
			items: [
				{
					title: t('homeView.onboarding.createFirstSku', 'Create your first SKU'),
					routeTo: Routes.INVENTORY_SKUS,
					onClick: () => {
						newOnboardingClickEvent({ action: 'SKU_CREATION' });
					},
					done: isOnboardingActionDone('SKU_CREATION', shopAnalytics),
				},
				{
					title: t(
						'homeView.onboarding.createFirstInventoryArticle',
						'Create your first inventory article',
					),
					routeTo: Routes.INVENTORY_ARTICLES_ADD,
					onClick: () => {
						newOnboardingClickEvent({ action: 'ARTICLE_CREATION' });
					},
					done: isOnboardingActionDone('ARTICLE_CREATION', shopAnalytics),
				},
				{
					title: t(
						'homeView.onboarding.connectProductToSku',
						'Connect your first product to an SKU',
					),
					routeTo: Routes.CatalogTab('products'),
					onClick: () => {
						newOnboardingClickEvent({ action: 'PRODUCT_SKU_CONNECTION' });
					},
					done: isOnboardingActionDone('PRODUCT_SKU_CONNECTION', shopAnalytics),
				},
			],
		},
		{
			name: t(
				'homeView.onboarding.addAndVerifyBusinessAndContactInformation',
				'Add and verify your business and contact information',
			),
			items: [
				{
					title: t(
						'homeView.onboarding.addStoreAddressAndContactDetails',
						'Add your store address and contact details',
					),
					routeTo: Routes.StoreTab('general'),
					onClick: () => {
						newOnboardingClickEvent({ action: 'STORE_DETAILS' });
					},
					done: isOnboardingActionDone('STORE_DETAILS', shopAnalytics),
				},
				{
					title: t('homeView.onboarding.openingHoursTitle2', 'Set your opening hours'),
					routeTo: Routes.StoreTab('opening-hours'),
					onClick: () => {
						newOnboardingClickEvent({ action: 'OPENING_HOURS' });
					},
					done: isOnboardingActionDone('OPENING_HOURS', shopAnalytics),
				},
				{
					title: t('homeView.onboarding.addBusinessId', 'Add your business ID'),
					routeTo: Routes.ACCOUNT_GENERAL,
					onClick: () => {
						newOnboardingClickEvent({ action: 'BUSINESS_ID' });
					},
					done: isOnboardingActionDone('BUSINESS_ID', shopAnalytics),
				},
				{
					title: t(
						'homeView.onboarding.verifyOfficialContactInformation',
						'Verify your official business contact information',
					),
					routeTo: Routes.ACCOUNT_GENERAL,
					onClick: () => {
						newOnboardingClickEvent({ action: 'BUSINESS_CONTACT_INFORMATION' });
						setOnboardingActionDone('BUSINESS_CONTACT_INFORMATION', shopAnalytics);
					},
					done: isOnboardingActionDone('BUSINESS_CONTACT_INFORMATION', shopAnalytics),
				},
			],
		},
		{
			name: t('homeView.onboarding.addWaiverAndPolicies', 'Add your waiver and policies'),
			items: [
				{
					title: t('homeView.onboarding.addPrivacyPolicy', 'Add your privacy policy'),
					routeTo: Routes.ACCOUNT_TERMS,
					onClick: () => {
						newOnboardingClickEvent({ action: 'PRIVACY_POLICY' });
					},
					done: isOnboardingActionDone('PRIVACY_POLICY', shopAnalytics),
				},
				{
					title: t('homeView.onboarding.addWaiver', 'Add your waiver'),
					routeTo: Routes.ACCOUNT_TERMS,
					onClick: () => {
						newOnboardingClickEvent({ action: 'TERMS_OF_SERVICE' });
					},
					done: isOnboardingActionDone('TERMS_OF_SERVICE', shopAnalytics),
				},
				{
					title: t('homeView.onboarding.addCancellationPolicy', 'Add your cancellation policy'),
					routeTo: Routes.ACCOUNT_TERMS,
					routeHash: '#cancellation-policy',
					onClick: () => {
						newOnboardingClickEvent({ action: 'CANCELLATION_POLICY' });
					},
					done: isOnboardingActionDone('CANCELLATION_POLICY', shopAnalytics),
				},
			],
		},
		{
			name: t(
				'homeView.onboarding.verifyOnlinePaymentMethods',
				'Verify your online payment methods',
			),
			items: [
				{
					title: t(
						'homeView.onboarding.verifyOnlinePaymentMethods',
						'Verify your online payment methods',
					),
					routeTo: Routes.ACCOUNT_PAYMENTS,
					onClick: () => {
						newOnboardingClickEvent({ action: 'PAYMENTS' });
						setOnboardingActionDone('PAYMENTS', shopAnalytics);
					},
					done: isOnboardingActionDone('PAYMENTS', shopAnalytics),
				},
			],
		},
		{
			name: t('homeView.onboarding.usersTitle2', 'Invite more users'),
			items: [
				{
					title: t('homeView.onboarding.usersTitle2', 'Invite more users'),
					routeTo: Routes.ACCOUNT_USERS,
					onClick: () => {
						newOnboardingClickEvent({ action: 'USERS' });
					},
					done: isOnboardingActionDone('USERS', shopAnalytics),
				},
			],
		},
		{
			name: t(
				'homeView.onboarding.linkOnlineStoreToWebsite',
				'Link your online store to your website',
			),
			items: [
				{
					title: t(
						'homeView.onboarding.linkOnlineStoreToWebsite',
						'Link your online store to your website',
					),
					routeTo: Routes.StoreTab('online-store'),
					onClick: () => {
						newOnboardingClickEvent({ action: 'LINK_ONLINE_STORE' });
						setOnboardingActionDone('LINK_ONLINE_STORE', shopAnalytics);
					},
					done: isOnboardingActionDone('LINK_ONLINE_STORE', shopAnalytics),
				},
			],
		},
	];
};
